import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../utils/store";
import MultipleSelectorField from "../products/MultipleSelectorField";

export type UploadSettingsData = {
  type_of_upload: string;
  prompt: string;
  translate_ai_text: boolean;
  pipeline: string;
  importer: string;
  product_labels: string[];
  product_namespace: string;
};

type PromptOption = { value: string; label: string };
type PromptOptGroup = { optgroup: string; options: PromptOption[] };
type PromptChoice = PromptOption | PromptOptGroup;

type FormChoices = {
  importers: Array<{ value: string; label: string }>;
  pipelines: Array<{ value: string; label: string }>;
  prompts: PromptChoice[];
  namespaces: Array<{ value: string; label: string }>;
  product_labels: Array<{ value: string; label: string }>;
  type_of_upload: Array<{ value: string; label: string }>;
};

type Props = {
  customerSlug?: string;
  onChange?: (settings: UploadSettingsData) => void;
  initialSettings?: Partial<UploadSettingsData>;
};

export const UploadSettings: React.FC<Props> = ({
  customerSlug,
  onChange,
  initialSettings = {},
}) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [settings, setSettings] = useState<UploadSettingsData>({
    type_of_upload: initialSettings.type_of_upload || "DEFAULT",
    prompt: initialSettings.prompt || "",
    translate_ai_text: initialSettings.translate_ai_text || false,
    pipeline: initialSettings.pipeline || "",
    importer: initialSettings.importer || "",
    product_labels: initialSettings.product_labels || [],
    product_namespace: initialSettings.product_namespace || "Default",
  });

  const [choices, setChoices] = useState<FormChoices | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const slug = customerSlug || "";
  const fetchChoices = async (): Promise<void> => {
    try {
      const response = await fetch(
        `/api/_internal/extract/upload_form_choices`,
        {
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load form choices");
      }

      const data = await response.json();
      setChoices(data.choices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch form choices on mount
  useEffect(() => {
    fetchChoices();
  }, []);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      onChange(settings);
    }
  }, [settings, onChange]);

  const handleSettingChange = (
    key: keyof UploadSettingsData,
    value: string | boolean | string[]
  ): void => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-red-600"></div>
      </div>
    );
  }

  if (error || !choices) {
    return (
      <div className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-sm">
        <p className="tw-text-red-800 tw-font-medium">Error loading form</p>
        <p className="tw-text-red-600">
          {error || "Failed to load form choices"}
        </p>
      </div>
    );
  }

  const showAiSettings = settings.type_of_upload === "AI";
  const showPipelineSettings = settings.type_of_upload === "PIPELINE";

  return (
    <div className="tw-w-full tw-max-w-[400px] tw-mx-auto tw-space-y-6">
      {/* Type of Upload */}
      <div className="tw-text-left">
        <h4 className="tw-text-base tw-font-medium tw-mb-4">
          What should happen after the upload?
        </h4>
        <div className="tw-space-y-2">
          {choices.type_of_upload.map((option) => {
            const isSelected = settings.type_of_upload === option.value;
            return (
              <label
                key={option.value}
                className={`tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-p-3 tw-border tw-rounded-sm ${
                  isSelected
                    ? "tw-bg-gray-100 tw-border-gray-400"
                    : "tw-border-gray-200 hover:tw-bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="type_of_upload"
                  value={option.value}
                  checked={isSelected}
                  onChange={(e): void =>
                    handleSettingChange("type_of_upload", e.target.value)
                  }
                  className="tw-w-4 tw-h-4"
                />
                <span className="tw-text-sm">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* AI Settings */}
      {showAiSettings && (
        <div className="tw-space-y-4 tw-p-4 tw-bg-gray-50 tw-rounded-sm">
          <h4 className="tw-text-base tw-font-medium">
            Select the type of text you want to generate
          </h4>
          <div>
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-block">
              Select prompt
            </label>
            <select
              value={settings.prompt}
              onChange={(e): void =>
                handleSettingChange("prompt", e.target.value)
              }
              className="txu-select tw-w-full"
            >
              <option value="">Select a prompt...</option>
              {choices.prompts.map((item) => {
                // Check if this is an optgroup or a regular option
                if ("optgroup" in item) {
                  return (
                    <optgroup key={item.optgroup} label={item.optgroup}>
                      {item.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                } else {
                  return (
                    <option key={item.value || "empty"} value={item.value}>
                      {item.label}
                    </option>
                  );
                }
              })}
            </select>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <input
              type="checkbox"
              id="translate_ai_text"
              checked={settings.translate_ai_text}
              onChange={(e): void =>
                handleSettingChange("translate_ai_text", e.target.checked)
              }
              className="tw-w-4 tw-h-4"
            />
            <label
              htmlFor="translate_ai_text"
              className="tw-text-sm tw-cursor-pointer"
            >
              Also translate using machine translation
            </label>
          </div>
        </div>
      )}

      {/* Pipeline Settings */}
      {showPipelineSettings && (
        <div className="tw-space-y-4 tw-p-4 tw-bg-gray-50 tw-rounded-sm tw-relative">
          <a
            href={`/d/${slug}/manage/pipelines`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-absolute tw-right-4 tw-top-4 tw-text-red-600 tw-text-sm tw-no-underline hover:tw-underline"
          >
            Open agent settings...
          </a>
          <div>
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-block">
              Select Agent
            </label>
            <select
              value={settings.pipeline}
              onChange={(e): void =>
                handleSettingChange("pipeline", e.target.value)
              }
              className="txu-select tw-w-full"
            >
              <option value="">Select an agent...</option>
              {choices.pipelines.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Advanced Settings */}
      <div className="tw-border-t tw-border-gray-200 tw-pt-4">
        <h4 className="tw-text-base tw-font-medium tw-mb-4">
          Advanced Settings
        </h4>
        <div className="tw-space-y-4">
          {/* Importer */}
          {choices.importers.length > 1 && (
            <div>
              <label className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-block">
                Importer
              </label>
              <select
                value={settings.importer}
                onChange={(e): void =>
                  handleSettingChange("importer", e.target.value)
                }
                className="txu-select tw-w-full"
              >
                {choices.importers.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Product Labels */}
          <div>
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-block">
              Add product labels
            </label>
            <MultipleSelectorField
              allowAdditions={true}
              availableChoices={choices.product_labels}
              disabled={false}
              initialData={settings.product_labels}
              onDataChanged={(_, data): void => {
                handleSettingChange("product_labels", data);
              }}
            />
          </div>

          {/* Namespace */}
          <div>
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-block">
              Select namespace
            </label>
            <div className="tw-flex tw-items-center tw-gap-2">
              <select
                value={settings.product_namespace}
                onChange={(e): void =>
                  handleSettingChange("product_namespace", e.target.value)
                }
                className="txu-select tw-flex-1"
              >
                {choices.namespaces.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <a
                href={`/d/${slug}/customer-fields`}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-red-600 tw-text-sm tw-no-underline hover:tw-underline tw-whitespace-nowrap"
              >
                Open PIM Fields settings...
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
